// © 2026 AttainIP Engineering
/**
 * Error Code → UX Mapping
 * Maps API error codes to user-friendly messages and actions
 */

import { appConfig } from '../config/appConfig';

export interface ErrorMapping {
  title: string;
  message: string;
  action?: 'retry' | 'reupload' | 'contact' | 'login';
}

const ERROR_MAPPINGS: Record<string, ErrorMapping> = {
  // Upload errors
  FILE_TOO_LARGE: {
    title: 'File Too Large',
    message: 'The file exceeds the 10MB limit. Please upload a smaller file.',
    action: 'reupload',
  },
  INVALID_FILE_TYPE: {
    title: 'Invalid File Type',
    message: 'Only CSV files are supported. Please upload a valid CSV file.',
    action: 'reupload',
  },
  INVALID_CSV_FORMAT: {
    title: 'Invalid CSV Format',
    message: 'The CSV file could not be parsed. Please check the format and try again.',
    action: 'reupload',
  },
  MISSING_REQUIRED_COLUMNS: {
    title: 'Missing Columns',
    message: 'Required columns are missing from the CSV. Please check the file format.',
    action: 'reupload',
  },

  // Report errors
  REPORT_NOT_FOUND: {
    title: 'Report Not Found',
    message: 'This report does not exist or has been deleted.',
  },
  REPORT_EXPIRED: {
    title: 'Report Expired',
    message: 'This report has expired after the 7-day retention period.',
    action: 'reupload',
  },

  // Share errors
  SHARE_LINK_EXPIRED: {
    title: 'Link Expired',
    message: 'This share link has expired. Please request a new link from the report owner.',
  },
  SHARE_LINK_NOT_FOUND: {
    title: 'Link Not Found',
    message: 'This share link is invalid or has been revoked.',
  },

  // Auth errors
  SESSION_EXPIRED: {
    title: 'Session Expired',
    message: 'Your session has expired. Please log in again.',
    action: 'login',
  },
  UNAUTHORIZED: {
    title: 'Not Authorized',
    message: 'You do not have permission to access this resource.',
  },
  INVALID_TOKEN: {
    title: 'Invalid Link',
    message: 'This login link is invalid or has already been used.',
    action: 'login',
  },
  TOKEN_EXPIRED: {
    title: 'Link Expired',
    message: 'This login link has expired. Please request a new one.',
    action: 'login',
  },
  RATE_LIMITED: {
    title: 'Too Many Requests',
    message: 'Please wait a moment before trying again.',
    action: 'retry',
  },
  INVALID_EMAIL: {
    title: 'Invalid Email',
    message: 'Please enter a valid email address.',
  },
  // Enterprise has no self-service purchase path — credit comes from the firm
  // pool an org admin tops up (RFC-0501). Telling those users to "purchase
  // more" points at a door that isn't there, so the remedy differs by flavour.
  INSUFFICIENT_CREDITS: {
    title: 'Insufficient Credits',
    message: appConfig.payments.enabled
      ? "You don't have enough credits for this analysis. Please purchase more."
      : "Your firm's credit pool doesn't have enough credits for this analysis. Please contact your organisation administrator.",
    action: 'retry',
  },
  // Emitted when an enterprise account hits a self-service payment endpoint
  // (RFC-0501 "No Payment for Enterprise"). Shouldn't be reachable now the UI
  // affordances are gated, but map it so it degrades to something meaningful.
  PAYMENT_NOT_AVAILABLE_FOR_ENTERPRISE: {
    title: 'Purchase Not Available',
    message: "Credits for your firm are managed by your organisation administrator, not bought individually.",
    action: 'contact',
  },

  // Server errors
  INTERNAL_ERROR: {
    title: 'Server Error',
    message: 'Something went wrong on our end. Please try again later.',
    action: 'retry',
  },
  SERVICE_UNAVAILABLE: {
    title: 'Service Unavailable',
    message: 'The service is temporarily unavailable. Please try again later.',
    action: 'retry',
  },

  // Default
  UNKNOWN_ERROR: {
    title: 'Error',
    message: 'An unexpected error occurred. Please try again.',
    action: 'retry',
  },
};

export function getErrorMapping(code: string): ErrorMapping {
  return ERROR_MAPPINGS[code] ?? ERROR_MAPPINGS['UNKNOWN_ERROR']!;
}
